import { inferSessionAccountPool } from "./sessionDefaults";
import { DEFAULT_PREFERRED_HOURS, getAccountCadence, type AccountCadence, type SchedulingSession } from "./engine/types";
import type { PublicationModel } from "../../types/models";

export type ScheduleScope = { kind: "folder"; id: string } | { kind: "video"; id: string };
export const scheduleScopeForVideo = (video: { id: string; folderId?: string | null }): ScheduleScope =>
  video.folderId ? { kind: "folder", id: video.folderId } : { kind: "video", id: video.id };

export type ScheduleConfigInput = {
  v: 1;
  accountIds: string[];
  startFrom: string;
  preferredHours: number[];
  cadences: Record<string, AccountCadence>;
  /** The cadences this session changed from what it opened with: the only ones the API remembers for the user. Not stored. */
  changedCadences?: Record<string, AccountCadence>;
};
export type ScheduleConfig = Omit<ScheduleConfigInput, "changedCadences"> & { savedAt: string };
export type StoredScheduleConfig = Pick<ScheduleConfig, "v" | "savedAt" | "accountIds"> &
  Partial<Pick<ScheduleConfig, "startFrom" | "preferredHours" | "cadences">>;
export type ParsedScheduleConfig =
  | { status: "missing"; config: null }
  | { status: "valid"; config: StoredScheduleConfig }
  | { status: "invalid" | "unsupported"; config: null };
/** The user's schedule defaults (`profiles.schedule_defaults`), which the API
 * keeps from their saved schedules: the accounts and hours of the last one, the
 * cadences of every account saved, and the providers connected at that save. A
 * new project opens with them. Never a start. */
export type ScheduleDefaults = {
  accountIds: string[];
  /** Missing: no provider counts as connected since. */
  providers?: string[];
  preferredHours?: number[];
  cadences: Record<string, AccountCadence>;
};

const record = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const isoDate = (value: unknown): value is string => {
  if (typeof value !== "string") return false;
  const parts = /^(\d{4})-(\d\d)-(\d\d)T(\d\d):(\d\d):(\d\d)(?:\.\d+)?(?:Z|[+-]\d\d:?\d\d)$/.exec(value);
  if (!parts || !Number.isFinite(Date.parse(value))) return false;
  const [year, month, day, hour, minute, second] = parts.slice(1).map(Number);
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return month >= 1 && month <= 12 && day >= 1 && day <= days[month - 1] && hour <= 23 && minute <= 59 && second <= 59;
};
export const validPreferredHours = (value: unknown): value is number[] =>
  Array.isArray(value) && value.length > 0 && value.every(hour => Number.isInteger(hour) && hour >= 0 && hour <= 23);
export const validAccountCadence = (value: unknown): value is AccountCadence => record(value) &&
  Number.isInteger(value.maxPostsPerDay) && Number(value.maxPostsPerDay) >= 1 && Number(value.maxPostsPerDay) <= 5 &&
  Array.isArray(value.allowedISODays) && value.allowedISODays.length > 0 &&
  value.allowedISODays.every(day => Number.isInteger(day) && day >= 1 && day <= 7) &&
  (value.preferredTimeSlotId === undefined || typeof value.preferredTimeSlotId === "string");
const sortedIds = (ids: readonly string[]) => [...new Set(ids)].sort();
const normalizeCadence = (cadence: AccountCadence): AccountCadence => ({
  maxPostsPerDay: cadence.maxPostsPerDay,
  allowedISODays: [...new Set(cadence.allowedISODays)].sort((a, b) => a - b),
  ...(cadence.preferredTimeSlotId === undefined ? {} : { preferredTimeSlotId: cadence.preferredTimeSlotId }),
});

export function parseScheduleConfig(value: unknown): ParsedScheduleConfig {
  if (value === null) return { status: "missing", config: null };
  if (!record(value)) return { status: "invalid", config: null };
  if (value.v !== 1) return { status: typeof value.v === "number" ? "unsupported" : "invalid", config: null };
  if (Object.keys(value).some(key => !["v", "savedAt", "accountIds", "startFrom", "preferredHours", "cadences"].includes(key)) ||
    !isoDate(value.savedAt) || !Array.isArray(value.accountIds) ||
    !value.accountIds.every(id => typeof id === "string" && id.length > 0) ||
    (value.startFrom !== undefined && !isoDate(value.startFrom)) ||
    (value.preferredHours !== undefined && !validPreferredHours(value.preferredHours)) ||
    (value.cadences !== undefined && (!record(value.cadences) || !Object.values(value.cadences).every(cadence =>
      validAccountCadence(cadence) && Object.keys(cadence).every(key => ["maxPostsPerDay", "allowedISODays", "preferredTimeSlotId"].includes(key)))))) {
    return { status: "invalid", config: null };
  }
  return { status: "valid", config: value as StoredScheduleConfig };
}

const nonEmptyStrings = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === "string" && item.length > 0);

/** Defaults only seed a new project, so unlike a resource config an unreadable
 * field is dropped on its own rather than refusing the whole value. */
export function parseScheduleDefaults(value: unknown): ScheduleDefaults | null {
  if (!record(value) || value.v !== 1) return null;
  return {
    accountIds: nonEmptyStrings(value.accountIds) ? value.accountIds : [],
    ...(nonEmptyStrings(value.providers) ? { providers: value.providers } : {}),
    ...(validPreferredHours(value.preferredHours) ? { preferredHours: value.preferredHours } : {}),
    cadences: record(value.cadences) ? Object.fromEntries(Object.entries(value.cadences)
      .filter((entry): entry is [string, AccountCadence] => validAccountCadence(entry[1]))
      .map(([id, cadence]) => [id, normalizeCadence(cadence)])) : {},
  };
}

const sameCadence = (a: AccountCadence, b: AccountCadence) => JSON.stringify(normalizeCadence(a)) === JSON.stringify(normalizeCadence(b));

export function buildScheduleConfig(session: SchedulingSession): ScheduleConfigInput {
  const accountIds = sortedIds(session.local.selectedAccountIds);
  const cadences = Object.fromEntries(accountIds.map(id => [id, normalizeCadence(getAccountCadence(session.accountCadences, id))]));
  return {
    v: 1, accountIds,
    startFrom: new Date(Math.floor(session.config.startFrom.getTime() / 60_000) * 60_000).toISOString(),
    preferredHours: [...session.config.preferredHours],
    cadences,
    changedCadences: Object.fromEntries(Object.entries(cadences)
      .filter(([id, cadence]) => !sameCadence(cadence, getAccountCadence(session.entryAccountCadences, id)))),
  };
}

export function scheduleConfigsEqual(a: StoredScheduleConfig | ScheduleConfigInput | null, b: StoredScheduleConfig | ScheduleConfigInput | null): boolean {
  if (!a || !b) return a === b;
  const comparable = (config: StoredScheduleConfig | ScheduleConfigInput) => ({
    v: config.v, accountIds: sortedIds(config.accountIds),
    startFrom: config.startFrom === undefined ? undefined : Math.floor(Date.parse(config.startFrom) / 60_000),
    preferredHours: config.preferredHours,
    cadences: config.cadences === undefined ? undefined : Object.fromEntries(Object.keys(config.cadences).sort()
      .map(id => [id, normalizeCadence(config.cadences![id])])),
  });
  return JSON.stringify(comparable(a)) === JSON.stringify(comparable(b));
}

/** Whether saving `config` over `stored` updates the user's defaults: the API
 * does when there was no config, anything but the start changed, or the
 * session explicitly changed a shared cadence. */
export const scheduleDefaultsChanged = (stored: StoredScheduleConfig | null, config: ScheduleConfigInput): boolean =>
  !stored || Object.keys(config.changedCadences ?? {}).length > 0 || !scheduleConfigsEqual({ ...stored, startFrom: config.startFrom }, config);

type DestinationAccount = { id: string; provider: string };
type SessionPublication = Pick<PublicationModel, "projectId" | "accountId"> & Partial<Pick<PublicationModel, "autoFolderId" | "status">>;

export function resolveSessionConfig({ stored, defaults = null, accounts, videoIds, publications, defaultDate, now }: {
  stored: StoredScheduleConfig | null;
  defaults?: ScheduleDefaults | null;
  accounts: readonly DestinationAccount[];
  videoIds: readonly string[];
  publications: readonly SessionPublication[];
  defaultDate?: Date;
  now: Date;
}) {
  const stableAccounts = [...accounts].sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  const storedIds = new Set(stored?.accountIds);
  const connectedIds = stableAccounts.filter(account => storedIds.has(account.id)).map(account => account.id);
  const scope = new Set(videoIds);
  const nonBlank = publications.some(pub => pub.projectId && scope.has(pub.projectId));
  const accountIds = stored && (nonBlank || connectedIds.length > 0) ? connectedIds
    : !nonBlank && defaults ? defaultAccountIds({ accounts: stableAccounts, defaults, videoIds, publications })
    : inferSessionAccountPool({ accounts: stableAccounts, videoIds, publications });
  // Every remembered cadence, not only those of the accounts connected now: an
  // account that arrives later (reconnected, or missing from a stale list) must
  // be scheduled and saved with its own cadence, not the engine default.
  // An account Auto post posts to (it holds a scheduled post of its) runs at
  // the user's cadence, so that is the one shown and edited here; the others
  // open on what this project saved.
  const autoPostAccounts = new Set(publications.filter(p => p.autoFolderId && p.status === "scheduled").map(p => p.accountId));
  const accountCadences = new Map<string, AccountCadence>();
  const cadenceIds = new Set([...stableAccounts.map(account => account.id),
    ...Object.keys(stored?.cadences ?? {}), ...Object.keys(defaults?.cadences ?? {})]);
  for (const id of cadenceIds) {
    const cadence = autoPostAccounts.has(id)
      ? defaults?.cadences[id] ?? stored?.cadences?.[id]
      : stored?.cadences?.[id] ?? defaults?.cadences[id];
    if (validAccountCadence(cadence)) accountCadences.set(id, normalizeCadence(cadence));
  }
  return {
    accountIds,
    startFrom: new Date(defaultDate ?? (stored?.startFrom && Date.parse(stored.startFrom) > now.getTime() ? stored.startFrom : now)),
    preferredHours: [...(stored?.preferredHours ?? defaults?.preferredHours ?? DEFAULT_PREFERRED_HOURS)],
    accountCadences,
  };
}

/** A new project's accounts: the saved ones still connected, plus one account
 * per provider connected since that save. When no saved account is connected
 * any more, one account per provider, as without defaults. */
function defaultAccountIds({ accounts, defaults, videoIds, publications }: {
  accounts: readonly DestinationAccount[];
  defaults: ScheduleDefaults;
  videoIds: readonly string[];
  publications: readonly SessionPublication[];
}): string[] {
  const savedIds = new Set(defaults.accountIds);
  const kept = accounts.filter(account => savedIds.has(account.id));
  if (kept.length === 0) return inferSessionAccountPool({ accounts, videoIds, publications });
  const known = new Set([...(defaults.providers ?? accounts.map(account => account.provider)), ...kept.map(account => account.provider)]);
  const added = new Set(inferSessionAccountPool({ accounts: accounts.filter(account => !known.has(account.provider)), videoIds, publications }));
  return accounts.filter(account => savedIds.has(account.id) || added.has(account.id)).map(account => account.id);
}
